import { dollarsToCents } from '@common/services';
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@common/hooks';
import { Button, Space, UploadFile } from 'antd';
import { isNil } from 'lodash';

import {
  DISPUTE_PREVIEW_INFO_QUERY,
  DisputeReviewQueryResponse,
  PerformDisputeAction,
} from '@pay-bill/graphql';
import { toISODateFormat } from '@common/services/datetime';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Colors } from '@common/tokens';
import { AutoAnimatedHeight } from 'works/legacy/components/auto-animated-height';
import {
  DisputeBodyDetails,
  DisputeCommentInput,
  DisputeDiffLine,
  DisputeDiffSection,
  DisputeFilesInput,
  DisputeFilesSection,
  DisputeValuesForm,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DuplicatedDisputeAlert,
  InvoiceItemDiffLine,
  SimpleDiffLine,
} from '@pay-bill/components';
import {
  useDisputeDeletedFiles,
  useDisputeRefetchs,
  useHasValidReopenDisputeFields,
} from '@pay-bill/hooks';
import { catchErrorWith, rejectSimpleErrors } from '@pay-bill/services';
import debounce from 'lodash/debounce';
import { HistoryDropDown, Form, Col, Row, Divider } from '@common/components';
type FormValues = {
  comment?: string;
  newHours?: number;
  billingAmount?: number;
  newPayCode?: string | undefined;
  finalRateCents?: number;
  files?: UploadFile[];
};

export const DisputeReopenDrawerBody = ({
  goBack,
  dispute,
}: {
  goBack: () => void;
  dispute: DisputeReviewQueryResponse['dispute'];
}) => {
  const [form] = Form.useForm();
  const [removedFiles, onFileDelete, maxFilesSlots] = useDisputeDeletedFiles(
    dispute.files.supplier
  );
  const [formValues, setFormValues] = useState<FormValues>(() => ({
    newHours: 0,
    newPayCode: undefined,
    finalRateCents: 0,
    files: [],
  }));

  const [performAction, { loading: loadingPerform }] =
    useMutation(PerformDisputeAction);

  const previewVariables = useMemo(() => {
    if (!formValues.newPayCode || isNil(formValues.newHours)) {
      return null;
    }

    return {
      newHours: formValues.newHours,
      newDate: toISODateFormat(dispute.newDate),
      newPayCode: formValues.newPayCode,
      finalRateCents: formValues.finalRateCents
        ? (dollarsToCents(formValues.finalRateCents) ?? undefined)
        : undefined,
      staffingRequestAssignmentId: dispute.staffingRequestAssignment.id,
    };
  }, [
    formValues.newPayCode,
    formValues.newHours,
    formValues.finalRateCents,
    dispute.newDate,
    dispute.staffingRequestAssignment.id,
  ]);

  const { data: previewData, loading: loadingPreview } = useQuery(
    DISPUTE_PREVIEW_INFO_QUERY,
    {
      skip: !previewVariables,
      variables: previewVariables!,
    }
  );

  const { previewRate, disputeMatches } = previewData || {};

  const formInvalid = !useHasValidReopenDisputeFields(form, formValues);
  const hasDuplication = useMemo(() => {
    if (disputeMatches?.dispute) {
      return disputeMatches.dispute.id !== dispute.id;
    }

    return !!disputeMatches?.invoiceItem;
  }, [dispute.id, disputeMatches?.dispute, disputeMatches?.invoiceItem]);
  const invoicePreCalculated = dispute.worksInvoice.preCalculated;

  const calculatedRate = previewRate?.finalRateCents || 0;
  const calculatedBillingAmount = previewRate?.billingAmountCents || 0;

  const refetchQueries = useDisputeRefetchs();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = (values: Record<string, any>) => {
    if (loadingPerform) {
      return;
    }

    performAction({
      variables: {
        disputeId: dispute.id,
        input: {
          action: 'reopen',
          newHours: values.newHours,
          newPayCode: values.newPayCode,
          finalRateCents: dollarsToCents(values.finalRateCents) ?? undefined,
          addedFiles: values.files || [],
          removedFiles: removedFiles.map((file) => file.id),
          comment: values.comment,
        },
      },
    })
      .then(rejectSimpleErrors('performDisputeAction'))
      .then(() => {
        createMessage('success', 'Dispute reopened');
        goBack();
        refetchQueries();
      })
      .catch(catchErrorWith('Failed to reopen dispute. Please try again.'));
  };

  function onResetBillingRate() {
    setFormValues({
      ...previewVariables,
      finalRateCents: undefined,
    });
  }

  const onFormChange = debounce(
    (changes: FormValues, formValues: FormValues) => {
      const updatedValues = {
        ...formValues,
        ...changes,
      };

      if (changes.newPayCode) {
        updatedValues.finalRateCents = undefined;
      }

      if (Array.isArray(updatedValues.newHours)) {
        updatedValues.newHours = updatedValues.newHours[1];
      }

      setFormValues(updatedValues);
    },
    500
  );

  return (
    <>
      <DrawerTitle>Reopen Dispute</DrawerTitle>
      <DrawerBody>
        <DisputeBodyDetails
          dispute={dispute}
          showStatus={false}
          showReason={false}
          showLastComment={false}
        />

        <Form
          onFinish={onFinish}
          layout="vertical"
          form={form}
          initialValues={formValues}
          onValuesChange={onFormChange}
        >
          <DisputeDiffSection mode="form">
            {dispute.disputeType === 'error' && dispute.worksInvoiceItem ? (
              <InvoiceItemDiffLine item={dispute.worksInvoiceItem} />
            ) : (
              <SimpleDiffLine.Empty />
            )}
            <DisputeDiffLine dispute={dispute} />
            <DisputeValuesForm
              form={form}
              payCodes={dispute.organization.payCodes}
              billingRate={calculatedRate}
              billingAmountCents={calculatedBillingAmount}
              showEstimationSymbol={invoicePreCalculated}
              onResetBillingRate={onResetBillingRate}
            />
            {invoicePreCalculated && (
              <p>
                * Billing Amount is an estimate and may differ from the final
                amount.
              </p>
            )}
            <AutoAnimatedHeight visible={hasDuplication}>
              <div style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <DuplicatedDisputeAlert
                      dispute={disputeMatches?.dispute}
                      invoiceItem={disputeMatches?.invoiceItem}
                    />
                  </Col>
                </Row>
              </div>
            </AutoAnimatedHeight>
          </DisputeDiffSection>
          <DisputeCommentInput />
          <Row className="dispute-upload-row">
            <Col span={24}>
              <DisputeFilesInput maxFilesSlots={maxFilesSlots} />
            </Col>
          </Row>
          <Divider gutter={16} />
          <Row>
            <DisputeFilesSection
              dispute={dispute}
              canDeleteFiles="Supplier"
              onDelete={onFileDelete}
              hiddenFiles={removedFiles}
            />
          </Row>
        </Form>
        <Divider gutter={16} />
        <HistoryDropDown data={dispute.history} hrVisible={false} />
      </DrawerBody>
      <DrawerFooter>
        <Space>
          <Col span={9}>
            <Button
              className="dispute-reopen-button"
              type="default"
              size="large"
              onClick={goBack}
            >
              Back
            </Button>
          </Col>
          <Col span={9}>
            <Button
              className="dispute-reopen-button"
              type="primary"
              size="large"
              onClick={form.submit}
              disabled={
                loadingPerform ||
                loadingPreview ||
                hasDuplication ||
                formInvalid
              }
            >
              Send Dispute
            </Button>
          </Col>
        </Space>
      </DrawerFooter>
      <style jsx>
        {`
          span.required-field {
            color: ${Colors.coral};
          }
          :global(.dispute-upload-row) {
            margin-top: 8px;
            max-width: 100%;
          }
        `}
      </style>
    </>
  );
};
